import request, { get, post } from '@/api/clients/portal-client'

// 获取项目列表
export function getProjectList() {
  return get('/pm/list')
}

// 新增项目
export function addProject(projectName) {
  return post('/pm/add', {
    projectName
  })
}

// 删除项目
export function deleteProject(id) {
  return post('/pm/delete', {
    id
  })
}

// 重命名项目
export function renameProject(id, newName) {
  return post('/pm/rename', {
    id,
    newName
  })
}

// 复制项目
export function copyProject(id) {
  return post('/pm/copy', {
    id
  })
}

// 保存问卷数据
export function saveQuestionnaire(projectId, content, silent = false) {
  const config = {}
  if (silent) {
    config.silent = true
  }
  return post('/pm/questionnaire/save', {
    projectId,
    content
  }, config)
}

// 获取问卷数据
export function getQuestionnaire(projectId, silent = false) {
  return get('/pm/questionnaire/get', {
    projectId
  }, {
    silent // 静默模式，不弹出错误提示
  })
}

// 公开获取问卷数据（无需登录）
export function getQuestionnairePublic(projectId, silent = false) {
  return get('/pm/questionnaire/public/get', {
    projectId
  }, {
    silent,
    skipAuthRedirect: true
  })
}

// 公开保存问卷数据（无需登录）
export function saveQuestionnairePublic(projectId, content, silent = false) {
  const config = {
    skipAuthRedirect: true
  }
  if (silent) {
    config.silent = true
  }
  return post('/pm/questionnaire/public/save', {
    projectId,
    content
  }, config)
}

// 获取项目任务结果列表
export function getTaskResultList(projectId, executorId = null, taskId = null) {
  const params = { projectId }
  if (executorId) {
    params.executorId = executorId
  }
  if (taskId) {
    params.taskId = taskId
  }
  return get('/pm/taskresult/list', params)
}

// 根据任务ID获取任务结果列表
export function getTaskResultListByTaskId(taskId) {
  return get('/pm/taskresult/list', {
    taskId
  })
}

// 删除项目任务结果
export function deleteTaskResult(id) {
  return post('/pm/taskresult/delete', {
    id
  })
}

// 更新项目任务结果状态
export function updateTaskResultStatus(id, status) {
  return post('/pm/taskresult/updateStatus', {
    id,
    status
  })
}

// 问卷内容经 DeepSeek 生成战略报告 JSON 并保存（后端 /api/ksai/pmQuestionnaireGeminiReport）
// 该接口使用 @RequestBody，必须用 application/json（与全局默认的 form-urlencoded 不同）
export function generatePmReportFromQuestionnaire(projectId, questionnaireContent, silent = false) {
  const config = {
    timeout: 15 * 60 * 1000,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  }
  if (silent) {
    config.silent = true
  }
  return post('/ksai/pmQuestionnaireGeminiReport', {
    projectId,
    questionnaireContent
  }, config)
}

// 保存报告数据
export function saveReport(projectId, content, silent = false) {
  const config = {}
  if (silent) {
    config.silent = true
  }
  return post('/pm/report/save', {
    projectId,
    content
  }, config)
}

// 获取报告数据
export function getReport(projectId, silent = false) {
  return get('/pm/report/get', {
    projectId
  }, {
    silent // 静默模式，不弹出错误提示
  })
}

// 公开获取报告数据（无需登录）
export function getReportPublic(projectId, silent = false) {
  return get('/pm/report/public/get', {
    projectId
  }, {
    silent,
    skipAuthRedirect: true
  })
}

// ==================== 任务管理 API ====================

// 获取任务列表
export function getTaskList(projectId = null) {
  const params = {}
  if (projectId) {
    params.projectId = projectId
  }
  return get('/pmtasks/list', params)
}

// 获取任务详情
export function getTaskInfo(id) {
  return get(`/pmtasks/info/${id}`)
}

// 新增任务
export function saveTask(data) {
  return request({
    url: '/pmtasks/save',
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  })
}

// 更新任务
export function updateTask(data) {
  return request({
    url: '/pmtasks/update',
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  })
}

// 删除任务
export function deleteTask(id) {
  return post('/pmtasks/delete', null, {
    params: { id }
  })
}

// ==================== 模板管理 API ====================

// 获取模板列表
export function getTemplateList(templateType = null) {
  const params = {}
  if (templateType) {
    params.templateType = templateType
  }
  return get('/pmtmpl/list', params)
}

// 新增模板
export function saveTemplate(data) {
  return request({
    url: '/pmtmpl/save',
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  })
}

// 更新模板
export function updateTemplate(data) {
  return request({
    url: '/pmtmpl/update',
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  })
}

// 删除模板
export function deleteTemplate(id) {
  return post('/pmtmpl/delete', null, {
    params: { id }
  })
}

// ==================== 我的任务管理 API ====================

// 获取我的任务列表
export function getMyTaskList(status = null, taskType = null) {
  const params = {}
  if (status !== null && status !== undefined && status !== '') {
    params.status = status
  }
  if (taskType) {
    params.taskType = taskType
  }
  return get('/pmmytasks/list', params)
}

// 获取我的任务详情
export function getMyTaskInfo(id) {
  return get(`/pmmytasks/info/${id}`)
}

// 更新任务状态
export function updateMyTaskStatus(id, status) {
  return post('/pmmytasks/updateStatus', null, {
    params: { id, status }
  })
}

// 获取任务统计信息
export function getMyTaskStatistics() {
  return get('/pmmytasks/statistics')
}

// 保存任务主角列表
export function saveTaskCharacters(taskId, characters) {
  return request({
    url: '/pmmytasks/saveCharacters',
    method: 'post',
    params: { taskId },
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(characters)
  })
}

// 保存任务素材列表
export function saveTaskMaterials(taskId, materials) {
  return request({
    url: '/pmmytasks/saveMaterials',
    method: 'post',
    params: { taskId },
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(materials)
  })
}

// 开始处理任务
export function startProcessTask(taskId) {
  return post('/pmmytasks/startProcess', null, {
    params: { taskId }
  })
}

// 完成任务
export function completeTask(taskId) {
  return post('/pmmytasks/completeTask', null, {
    params: { taskId }
  })
}

// 拒绝任务
export function rejectTask(taskId) {
  return post('/pmmytasks/rejectTask', null, {
    params: { taskId }
  })
}

// 重新执行任务（状态改为3，删除 isMerging 参数）
export function reExecuteTask(taskId) {
  return post('/pmmytasks/reExecute', null, {
    params: { taskId }
  })
}

// 提交任务结果（批量将任务结果状态改为2-已提交）
export function submitTaskResults(taskId) {
  return post('/pmmytasks/submitResults', null, {
    params: { taskId }
  })
}

// ==================== 视频脚本管理 API ====================

// 获取视频脚本列表
export function getVideoScriptList(projectId) {
  return get('/pmvideoscript/list', { projectId })
}

// 修改视频脚本
export function updateVideoScript(data) {
  return request({
    url: '/pmvideoscript/update',
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(data)
  })
}

// 删除视频脚本
export function deleteVideoScript(id) {
  return post('/pmvideoscript/delete', null, {
    params: { id }
  })
}

// 批量删除视频脚本
export function batchDeleteVideoScript(ids) {
  return request({
    url: '/pmvideoscript/batchDelete',
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(ids || [])
  })
}

// 发布视频脚本
export function publishVideoScript(id) {
  return post('/pmvideoscript/publish', null, {
    params: { id }
  })
}

// 撤回发布视频脚本
export function withdrawVideoScript(id) {
  return post('/pmvideoscript/withdraw', null, {
    params: { id }
  })
}

// 从视频脚本创建自动任务
export function createAutoTaskFromVideoScript(data) {
  return post('/pmvideoscript/createAutoTask', data, {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  })
}

// 自动任务列表
export function getAutoTaskList(params = {}) {
  return get('/pmvideoscript/autoTask/list', params)
}

// 自动任务算力配置，前端仅用于展示和即时校验；后端仍按同一配置做最终校验。
export function getAutoTaskPowerConfig() {
  return get('/pmvideoscript/autoTask/powerConfig')
}

// 暂停自动任务；暂停后才允许修改或删除。
export function pauseAutoTask(id) {
  return post('/pmvideoscript/autoTask/pause', null, {
    params: { id }
  })
}

// 修改已暂停的自动任务，保存后后端会重新放入执行队列。
export function updateAutoTask(data) {
  return post('/pmvideoscript/autoTask/update', data, {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  })
}

// 删除已暂停的自动任务。
export function deleteAutoTask(id) {
  return post('/pmvideoscript/autoTask/delete', null, {
    params: { id }
  })
}

// 重新执行自动任务；保留原参数，重新进入后台队列。
export function retryAutoTask(id) {
  return post('/pmvideoscript/autoTask/retry', null, {
    params: { id }
  })
}

// ==================== Gemini视频脚本生成 ====================
// 项目「问卷+报告+知识库+参数」调用 Gemini 生成视频脚本并保存（后端 /api/ksai/pmVideoScriptGeminiCreate）
// 该接口使用 @RequestBody，必须用 application/json
export function generatePmVideoScriptFromProject(data, silent = false) {
  const config = {
    timeout: 15 * 60 * 1000,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  }
  if (silent) {
    config.silent = true
  }
  return post('/ksai/pmVideoScriptGeminiCreate', data, config)
}

// 项目「问卷+报告+参数」调用内置 Agent 生成视频脚本并保存
export function generatePmVideoScriptAgentCreate(data, silent = false) {
  const config = {
    timeout: 15 * 60 * 1000,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  }
  if (silent) {
    config.silent = true
  }
  return post('/ksai/pmVideoScriptAgentCreate', data, config)
}

// 第一段：严格基于知识库检索视频脚本框架名称
export function generatePmVideoScriptFrameworks(data, silent = false) {
  const config = {
    timeout: 5 * 60 * 1000,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  }
  if (silent) {
    config.silent = true
  }
  return post('/ksai/pmVideoScriptFrameworks', data, config)
}
